// src/pages/Usuarios.jsx
import { useState, useEffect } from "react";
import { useAuthStore } from "../store/auth.store";
import api from "../api/axios";
import "./Usuarios.css";
import TecnicoPermisos from "../components/tickets/TecnicoPermisos";
import CalendarioPermisos from "../components/tickets/CalendarioPermisos";

// ✅ Estados de disponibilidad para técnicos
const DISPONIBILIDAD_ESTADOS = [
  { value: 'DISPONIBLE', label: '🟢 Disponible', color: 'success' },
  { value: 'OCUPADO', label: '🟡 Ocupado', color: 'warning' },
  { value: 'PERMISO_SOLICITADO', label: '🔵 Permiso Solicitado', color: 'info' },
  { value: 'PERMISO_APROBADO', label: '🟢 Permiso Aprobado', color: 'success' },
  { value: 'PERMISO_RECHAZADO', label: '🔴 Permiso Rechazado', color: 'error' }
];

export default function Usuarios() {
  const { token, user } = useAuthStore((s) => s);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Form para crear usuario
  const [newUser, setNewUser] = useState({
    username: "",
    email: "",
    password: "",
  });

  // Form para editar usuario
  const [editData, setEditData] = useState(null);

  // Modal de token
  const [modalToken, setModalToken] = useState(null);
  const [modalUser, setModalUser] = useState(null);

  // ✅ Estados para disponibilidad
  const [disponibilidadDialog, setDisponibilidadDialog] = useState(null);
  const [savingDisponibilidad, setSavingDisponibilidad] = useState(false);

  // ✅ Estados para permisos por fecha
  const [permisosDialogOpen, setPermisosDialogOpen] = useState(false);
  const [selectedTecnicoForPermisos, setSelectedTecnicoForPermisos] = useState(null);

  // ✅ Estados para calendario de permisos (FASE 3)
  const [calendarioPermisosOpen, setCalendarioPermisosOpen] = useState(false);

  // ✅ Verificar si el usuario actual es admin o supervisor
  const userRoles = user?.roles || [];
  const isAdmin = userRoles.some(r => r === 'admin' || r?.name === 'admin');
  const isSupervisor = userRoles.some(r => r === 'supervisor' || r?.name === 'supervisor');
  const canManageAvailability = isAdmin || isSupervisor;

  // Obtener usuarios desde backend
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/users", {
        headers: { Authorization: `Bearer ${token}` },
      });

      // ✅ Los usuarios ya vienen con disponibilidad desde el backend
      const usersWithAvailability = data.map((u) => {
        const isTecnico = u.roles?.some(r => r.name === 'tecnico_campo' || r.name === 'soporte_n2');
        if (isTecnico) {
          return {
            ...u,
            disponibilidad_estado: u.disponibilidad_estado || 'DISPONIBLE',
            disponibilidad_motivo: u.disponibilidad_motivo || null
          };
        }
        return { ...u, disponibilidad_estado: null, disponibilidad_motivo: null };
      });

      setUsers(usersWithAvailability);
    } catch (err) {
      console.error(err);
      setError("No se pudieron cargar los usuarios");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchUsers();
  }, [token]);

  // Crear nuevo usuario
  const handleCreateUser = async () => {
    if (!newUser.username || !newUser.password || !newUser.email) {
      setError("Debe ingresar usuario, email y contraseña");
      return;
    }
    try {
      setLoading(true);
      const { data } = await api.post("/users", newUser, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsers((prev) => [...prev, data]);
      setNewUser({ username: "", email: "", password: "" });
      setError(null);
    } catch (err) {
      console.error(err);
      setError("No se pudo crear el usuario");
    } finally {
      setLoading(false);
    }
  };

  // Actualizar usuario
  const handleUpdateUser = async () => {
    if (!editData.username || !editData.email) {
      setError("Debe ingresar usuario y email");
      return;
    }
    try {
      setLoading(true);
      const { data } = await api.put(`/users/${editData.id}`, editData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsers((prev) =>
        prev.map((u) => (u.id === data.id ? data : u))
      );
      setEditData(null);
      setError(null);
    } catch (err) {
      console.error(err);
      setError("No se pudo actualizar el usuario");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Activar/Desactivar usuario (con query params)
  const toggleUserActive = async (user) => {
    try {
      setLoading(true);

      const nuevoEstado = !user.is_active;

      // 1. Cambiar estado activo/inactivo
      const { data } = await api.put(
        `/users/${user.id}`,
        { is_active: nuevoEstado },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // 2. Si es técnico, actualizar disponibilidad automáticamente
      const isTecnico = user.roles?.some(r => r.name === 'tecnico_campo' || r.name === 'soporte_n2');
      if (isTecnico) {
        const estadoDisponibilidad = nuevoEstado ? 'DISPONIBLE' : 'OCUPADO';
        const motivo = nuevoEstado ? 'Usuario reactivado' : 'Usuario desactivado';

        await api.post(
          `/tickets/tecnicos/${user.id}/cambiar-disponibilidad`,
          null,
          {
            params: {
              estado: estadoDisponibilidad,
              motivo: motivo
            },
            headers: { Authorization: `Bearer ${token}` }
          }
        );
      }

      // 3. Actualizar la lista de usuarios
      await fetchUsers();
      setError(null);
    } catch (err) {
      console.error(err);
      console.error('Error response:', err.response?.data);
      setError(err.response?.data?.detail || "No se pudo cambiar el estado del usuario");
    } finally {
      setLoading(false);
    }
  };

  // Eliminar usuario
  const handleDeleteUser = async (user) => {
    if (!window.confirm(`¿Deseas eliminar al usuario ${user.username}?`)) return;
    try {
      setLoading(true);
      await api.delete(`/users/${user.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsers((prev) => prev.filter((u) => u.id !== user.id));
      setError(null);
    } catch (err) {
      console.error(err);
      setError("No se pudo eliminar el usuario");
    } finally {
      setLoading(false);
    }
  };

  // Generar token bajo demanda y mostrar modal
  const handleShowToken = async (userId) => {
    try {
      setLoading(true);
      const { data } = await api.post(
        `/users/${userId}/generate-token`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setModalToken(data.token);
      const selectedUser = users.find((u) => u.id === userId);
      setModalUser(selectedUser);
    } catch (err) {
      console.error(err);
      alert("No se pudo generar el token");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Verificar si un usuario es técnico
  const isTecnico = (user) => {
    return user?.roles?.some(r => r.name === 'tecnico_campo' || r.name === 'soporte_n2');
  };

  // ✅ Obtener el color del estado de disponibilidad
  const getEstadoColor = (estado) => {
    const found = DISPONIBILIDAD_ESTADOS.find(e => e.value === estado);
    return found?.color || 'default';
  };

  // ✅ Obtener la etiqueta del estado de disponibilidad
  const getEstadoLabel = (estado) => {
    const found = DISPONIBILIDAD_ESTADOS.find(e => e.value === estado);
    return found?.label || estado || 'DISPONIBLE';
  };

  // ✅ Abrir diálogo de disponibilidad
  const openDisponibilidadDialog = (user) => {
    setDisponibilidadDialog({
      user: user,
      estado: user.disponibilidad_estado || 'DISPONIBLE',
      motivo: user.disponibilidad_motivo || ''
    });
  };

  // ✅ Abrir diálogo de permisos por fecha
  const handleOpenPermisos = (user) => {
    setSelectedTecnicoForPermisos(user);
    setPermisosDialogOpen(true);
  };

  // ✅ Abrir calendario de permisos (FASE 3)
  const handleOpenCalendarioPermisos = () => {
    setCalendarioPermisosOpen(true);
  };

  // ✅ Cerrar diálogo de disponibilidad
  const closeDisponibilidadDialog = () => {
    setDisponibilidadDialog(null);
    setSavingDisponibilidad(false);
  };

  // ✅ Guardar disponibilidad (con query params)
  const saveDisponibilidad = async () => {
    if (!disponibilidadDialog) return;

    setSavingDisponibilidad(true);
    try {
      await api.post(
        `/tickets/tecnicos/${disponibilidadDialog.user.id}/cambiar-disponibilidad`,
        null,
        {
          params: {
            estado: disponibilidadDialog.estado,
            motivo: disponibilidadDialog.motivo || null
          },
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      await fetchUsers();
      closeDisponibilidadDialog();
    } catch (err) {
      console.error(err);
      console.error('Error response:', err.response?.data);
      setError(err.response?.data?.detail || "No se pudo cambiar la disponibilidad del técnico");
    } finally {
      setSavingDisponibilidad(false);
    }
  };

  if (loading && users.length === 0) {
    return (
      <div className="usuarios-loading-container">
        <div className="usuarios-loading-spinner"></div>
        <p>Cargando usuarios...</p>
      </div>
    );
  }

  return (
    <div className="usuarios-container">
      {/* Header */}
      <div className="usuarios-header">
        <div className="header-left">
          <div className="header-icon">👥</div>
          <div>
            <h1>Usuarios del Sistema</h1>
            <p>Gestión de usuarios y credenciales</p>
          </div>
        </div>
        <div className="header-stats" style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <span className="stats-badge">Total: {users.length} usuarios</span>
          {canManageAvailability && (
            <button
              onClick={handleOpenCalendarioPermisos}
              className="btn-calendario-permisos"
            >
              📅 Calendario de Permisos
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="error-message">
          <span className="error-icon">❌</span>
          <span>{error}</span>
        </div>
      )}

      {/* Form Crear Usuario */}
      <div className="usuarios-card">
        <div className="card-header">
          <span className="card-icon">➕</span>
          <h2>Crear Nuevo Usuario</h2>
        </div>
        <div className="card-body">
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label required">
                Usuario <span className="required-star">*</span>
              </label>
              <input
                type="text"
                placeholder="Ej: jperez"
                value={newUser.username}
                onChange={(e) =>
                  setNewUser({ ...newUser, username: e.target.value })
                }
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label required">
                Email <span className="required-star">*</span>
              </label>
              <input
                type="email"
                placeholder="ejemplo@correo.com"
                value={newUser.email}
                onChange={(e) =>
                  setNewUser({ ...newUser, email: e.target.value })
                }
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label required">
                Contraseña <span className="required-star">*</span>
              </label>
              <input
                type="password"
                placeholder="••••••••"
                value={newUser.password}
                onChange={(e) =>
                  setNewUser({ ...newUser, password: e.target.value })
                }
                className="form-input"
              />
            </div>
          </div>

          <button
            onClick={handleCreateUser}
            disabled={loading}
            className="btn-primary btn-block"
          >
            {loading ? (
              <>
                <span className="loading-spinner-small"></span>
                Creando...
              </>
            ) : (
              "Crear Usuario"
            )}
          </button>
        </div>
      </div>

      {/* Form Editar Usuario */}
      {editData && (
        <div className="usuarios-card edit-card">
          <div className="card-header">
            <span className="card-icon">✏️</span>
            <h2>Editar Usuario: {editData.username}</h2>
          </div>
          <div className="card-body">
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label required">Usuario</label>
                <input
                  type="text"
                  value={editData.username}
                  onChange={(e) =>
                    setEditData({ ...editData, username: e.target.value })
                  }
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label className="form-label required">Email</label>
                <input
                  type="email"
                  value={editData.email}
                  onChange={(e) =>
                    setEditData({ ...editData, email: e.target.value })
                  }
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label className="form-label">
                  Nueva Contraseña <span className="optional">(opcional)</span>
                </label>
                <input
                  type="password"
                  placeholder="Dejar en blanco para no cambiar"
                  value={editData.password || ""}
                  onChange={(e) =>
                    setEditData({ ...editData, password: e.target.value })
                  }
                  className="form-input"
                />
              </div>
            </div>

            <div className="edit-actions">
              <button
                onClick={handleUpdateUser}
                disabled={loading}
                className="btn-success"
              >
                {loading ? "Actualizando..." : "Actualizar Usuario"}
              </button>
              <button
                onClick={() => setEditData(null)}
                className="btn-secondary"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tabla de usuarios */}
      <div className="usuarios-table-container">
        <table className="usuarios-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Usuario</th>
              <th>Email</th>
              <th>Roles</th>
              <th>Estado</th>
              {canManageAvailability && (
                <th className="text-center">Disponibilidad</th>
              )}
              <th>Token</th>
              <th className="text-center">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 ? (
              <tr>
                <td colSpan={canManageAvailability ? 8 : 7} className="empty-state">
                  <div className="empty-icon">👥</div>
                  <p className="empty-title">No hay usuarios registrados</p>
                  <p className="empty-subtitle">Crea tu primer usuario para comenzar</p>
                </td>
              </tr>
            ) : (
              users.map((u) => {
                const tecnico = isTecnico(u);
                return (
                  <tr key={u.id} className="user-row">
                    <td className="user-id">#{u.id}</td>
                    <td>
                      <div className="user-info">
                        <span className="user-avatar">
                          {u.username?.charAt(0).toUpperCase() || '👤'}
                        </span>
                        <span className="user-username">{u.username}</span>
                      </div>
                    </td>
                    <td className="user-email">{u.email}</td>
                    <td>
                      {u.roles?.map((role) => (
                        <span
                          key={role.id}
                          className={`role-badge ${role.name === 'admin' ? 'role-admin' : role.name === 'tecnico_campo' ? 'role-tecnico' : 'role-default'}`}
                        >
                          {role.name}
                        </span>
                      ))}
                    </td>
                    <td>
                      <span className={`status-badge ${u.is_active ? 'active' : 'inactive'}`}>
                        {u.is_active ? '✅ Activo' : '❌ Inactivo'}
                      </span>
                    </td>
                    {canManageAvailability && (
                      <td className="text-center">
                        {tecnico ? (
                          u.is_active ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'center' }}>
                              <button
                                onClick={() => openDisponibilidadDialog(u)}
                                className={`btn-disponibilidad ${getEstadoColor(u.disponibilidad_estado)}`}
                                title="Cambiar disponibilidad del técnico"
                                style={{ width: '100%' }}
                              >
                                {getEstadoLabel(u.disponibilidad_estado)}
                              </button>
                              <button
                                onClick={() => handleOpenPermisos(u)}
                                className="btn-permisos"
                                title="Gestionar permisos por fecha"
                                style={{
                                  padding: '2px 8px',
                                  border: 'none',
                                  borderRadius: '4px',
                                  fontSize: '10px',
                                  cursor: 'pointer',
                                  backgroundColor: '#e3f2fd',
                                  color: '#0d47a1',
                                  transition: 'all 0.2s',
                                  width: '100%'
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.backgroundColor = '#bbdefb';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.backgroundColor = '#e3f2fd';
                                }}
                              >
                                📅 Permisos
                              </button>
                            </div>
                          ) : (
                            <span className="text-muted" style={{ color: '#dc2626', fontWeight: 'bold' }}>
                              🔴 INACTIVO
                            </span>
                          )
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                    )}
                    <td>
                      <button
                        onClick={() => handleShowToken(u.id)}
                        className="btn-token"
                        title="Generar y mostrar token"
                      >
                        🔑 Token
                      </button>
                    </td>
                    <td className="user-actions">
                      <button
                        onClick={() =>
                          setEditData({
                            id: u.id,
                            username: u.username,
                            email: u.email,
                            password: "",
                            is_active: u.is_active,
                          })
                        }
                        className="action-btn edit"
                        title="Editar usuario"
                      >
                        ✏️
                      </button>
                      <button
                        onClick={() => toggleUserActive(u)}
                        className={`action-btn ${u.is_active ? 'deactivate' : 'activate'}`}
                        title={u.is_active ? "Desactivar usuario" : "Activar usuario"}
                      >
                        {u.is_active ? '🔴' : '🟢'}
                      </button>
                      <button
                        onClick={() => handleDeleteUser(u)}
                        className="action-btn delete"
                        title="Eliminar usuario"
                      >
                        🗑️
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* ✅ Modal para disponibilidad de técnicos */}
      {disponibilidadDialog && (
        <div className="modal-overlay" onClick={closeDisponibilidadDialog}>
          <div className="modal-content modal-medium" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-header-left">
                <div className="modal-header-icon">🟢</div>
                <div>
                  <h3>Disponibilidad del Técnico</h3>
                  <p>Usuario: {disponibilidadDialog.user?.username}</p>
                </div>
              </div>
              <button
                onClick={closeDisponibilidadDialog}
                className="modal-close"
              >
                ✕
              </button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label className="form-label required">
                  Estado de Disponibilidad <span className="required-star">*</span>
                </label>
                <select
                  value={disponibilidadDialog.estado}
                  onChange={(e) =>
                    setDisponibilidadDialog({
                      ...disponibilidadDialog,
                      estado: e.target.value
                    })
                  }
                  className="form-select"
                >
                  {DISPONIBILIDAD_ESTADOS.map((estado) => (
                    <option key={estado.value} value={estado.value}>
                      {estado.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Motivo <span className="optional">(opcional)</span></label>
                <textarea
                  value={disponibilidadDialog.motivo || ''}
                  onChange={(e) =>
                    setDisponibilidadDialog({
                      ...disponibilidadDialog,
                      motivo: e.target.value
                    })
                  }
                  className="form-textarea"
                  rows="3"
                  placeholder="Ej: Permiso médico, Capacitación, Descanso, etc."
                />
              </div>

              {disponibilidadDialog.estado === 'PERMISO_SOLICITADO' && (
                <div className="info-message info">
                  🔵 El técnico ha solicitado permiso. Debe ser aprobado por un supervisor o administrador.
                </div>
              )}

              {disponibilidadDialog.estado === 'PERMISO_RECHAZADO' && (
                <div className="info-message warning">
                  🔴 El permiso ha sido rechazado. El técnico debe estar disponible para trabajar.
                </div>
              )}

              {disponibilidadDialog.estado === 'OCUPADO' && (
                <div className="info-message warning">
                  🟡 El técnico está ocupado. No se le asignarán nuevos tickets hasta que cambie su estado.
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button
                onClick={closeDisponibilidadDialog}
                className="btn-secondary"
                disabled={savingDisponibilidad}
              >
                Cancelar
              </button>
              <button
                onClick={saveDisponibilidad}
                className="btn-primary"
                disabled={savingDisponibilidad}
              >
                {savingDisponibilidad ? (
                  <>
                    <span className="loading-spinner-small"></span>
                    Guardando...
                  </>
                ) : (
                  'Guardar'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ✅ Modal para permisos por fecha de técnicos */}
      <TecnicoPermisos
        open={permisosDialogOpen}
        onClose={() => {
          setPermisosDialogOpen(false);
          setSelectedTecnicoForPermisos(null);
          fetchUsers();
        }}
        tecnicoId={selectedTecnicoForPermisos?.id}
        tecnicoNombre={selectedTecnicoForPermisos?.username}
      />

      {/* ✅ Modal para calendario de permisos (FASE 3) */}
      <CalendarioPermisos
        open={calendarioPermisosOpen}
        onClose={() => setCalendarioPermisosOpen(false)}
      />

      {/* Modal para mostrar token */}
      {modalToken && modalUser && (
        <div className="modal-overlay" onClick={() => setModalToken(null)}>
          <div className="modal-content modal-token" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-header-left">
                <div className="modal-header-icon">🔑</div>
                <div>
                  <h3>Token de Acceso</h3>
                  <p>Usuario: {modalUser.username}</p>
                </div>
              </div>
              <button
                onClick={() => setModalToken(null)}
                className="modal-close"
              >
                ✕
              </button>
            </div>

            <div className="modal-body">
              <p className="token-label">Token generado:</p>
              <div className="token-container">
                <textarea
                  readOnly
                  value={modalToken}
                  className="token-textarea"
                  spellCheck="false"
                />
              </div>
              <p className="token-warning">
                ⚠️ Este token solo se muestra una vez. Guárdalo en un lugar seguro.
              </p>
            </div>

            <div className="modal-footer">
              <button
                onClick={() => navigator.clipboard.writeText(modalToken)}
                className="btn-copy"
              >
                📋 Copiar token
              </button>
              <button
                onClick={() => setModalToken(null)}
                className="btn-secondary"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
